/**
 * Policy Toggles Domain
 * Store and handlers for managing policy enabled/disabled states.
 *
 * One switch is on its way at a time: the store holds which (`pendingId`), so
 * the page can rest every switch and show the pressed one waiting. A refused
 * switch is said in a toast, in the player's language where the reason is
 * one a player can meet.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import i18n from '../i18n/config';
import { OpCode, MessageType } from '../constants/op-codes';
import { sendGatedCommand } from './command-gate';
import { getViewerUserId } from './current-team';
import { showErrorToast } from './toasts';
import { refusalKey, refusalText, type OrderRefusal } from './order-block';

// ==================== TYPES ====================

interface PolicyStatesSyncMessage {
  type: string;
  cohort_id: string;
  policy_states: Record<string, boolean>;
}

interface TogglePolicySuccessMessage {
  type: string;
  /** Who switched it; the answer to our own switch is the one that ends our wait. */
  sender_user_id?: string;
  cohort_id: string;
  innovation_id: string;
  enabled: boolean;
}

// ==================== STORE ====================

interface PolicyToggleState {
  /** Map of policy enabled states by team (cohort_id -> innovation_id -> enabled) */
  policyStatesByTeam: Record<string, Record<string, boolean>>;
  /** The policy whose switch is on its way to the server, or null. */
  pendingId: string | null;
  
  // Actions
  setPolicyStates: (cohortId: string, states: Record<string, boolean>) => void;
  setPolicyEnabled: (cohortId: string, innovationId: string, enabled: boolean) => void;
  setPendingId: (innovationId: string | null) => void;
  reset: () => void;
}

const initialState = {
  policyStatesByTeam: {} as Record<string, Record<string, boolean>>,
  pendingId: null as string | null,
};

export const usePolicyToggleStore = create<PolicyToggleState>()(
  devtools(
    (set) => ({
      ...initialState,

      setPolicyStates: (cohortId, states) => {
        set(
          (state) => ({
            policyStatesByTeam: {
              ...state.policyStatesByTeam,
              [cohortId]: states,
            },
          }),
          false,
          'setPolicyStates'
        );
      },

      setPolicyEnabled: (cohortId, innovationId, enabled) => {
        set(
          (state) => {
            const existing = state.policyStatesByTeam[cohortId] || {};
            return {
              policyStatesByTeam: {
                ...state.policyStatesByTeam,
                [cohortId]: {
                  ...existing,
                  [innovationId]: enabled,
                },
              },
            };
          },
          false,
          'setPolicyEnabled'
        );
      },

      setPendingId: (innovationId) => {
        set({ pendingId: innovationId }, false, 'setPendingId');
      },
      reset: () => {
        set(initialState, false, 'reset');
      },
    }),
    {
      name: 'policy-toggle-store',
    }
  )
);

// ==================== HANDLERS ====================

/**
 * Handle policy states sync from server (on join)
 */
export function handlePolicyStatesSync(data: PolicyStatesSyncMessage) {
  console.log('[PolicyToggleHandler] Policy states synced for team:', data.cohort_id);
  
  const store = usePolicyToggleStore.getState();
  store.setPolicyStates(data.cohort_id, data.policy_states || {});
}

/**
 * Handle successful policy toggle
 */
export function handleTogglePolicySuccess(data: TogglePolicySuccessMessage) {
  console.log('[PolicyToggleHandler] Policy toggled:', data.innovation_id, 'enabled:', data.enabled);
  const store = usePolicyToggleStore.getState();
  store.setPolicyEnabled(data.cohort_id, data.innovation_id, data.enabled);
  // Only the answer to our own switch ends the wait: the command gate waits for
  // the same one, by the same test, so the two cannot come apart.
  if (data.sender_user_id !== undefined && data.sender_user_id === getViewerUserId()) {
    store.setPendingId(null);
  }
}

/**
 * The refusals of a switch a player can meet, by the code the server sends
 * them with (innovation.go, TogglePolicyReason…), and what each says. A Go
 * test holds this table to the server's codes (client_parity_test.go).
 */
const POLICY_REFUSALS = {
  // A policy not unlocked: the page was behind the server.
  not_unlocked: 'hq.policies.notUnlocked',
} as const;

/**
 * Handle failed policy toggle: say so in a toast. A refusal a player can meet
 * comes with a `reason` and is said in their language; the rest mean a
 * malformed request and keep the server's words.
 */
export function handleTogglePolicyFailed(data: OrderRefusal) {
  console.error('[PolicyToggleHandler] Policy toggle failed:', data.error);
  const store = usePolicyToggleStore.getState();
  const policy = store.pendingId;
  store.setPendingId(null);
  const known = refusalKey(POLICY_REFUSALS, data.reason);
  showErrorToast(
    policy
      ? i18n.t('hq.policies.switchFailed', { name: i18n.t(`innovation.nodes.${policy}.name`) })
      : i18n.t('hq.policies.switchFailedAny'),
    refusalText(known ? { key: known } : null, data),
  );
}

// ==================== SERVICE FUNCTIONS ====================

/**
 * Request to toggle a policy on/off
 * 
 * @param innovationId - ID of the policy innovation to toggle
 * @param enabled - New enabled state
 */
export async function togglePolicy(innovationId: string, enabled: boolean): Promise<void> {
  const store = usePolicyToggleStore.getState();
  store.setPendingId(innovationId);

  const message = {
    type: MessageType.MessageTypeTogglePolicy,
    innovation_id: innovationId,
    enabled: enabled,
  };

  console.log('[PolicyToggleService] Requesting toggle:', innovationId, 'enabled:', enabled);

  await sendGatedCommand(OpCode.OpCodeTogglePolicy, JSON.stringify(message), {
    endWait: () => store.setPendingId(null),
    notSent: handleTogglePolicyFailed,
  });
}
